import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

const ThreeDViewer = ({ modelUrl, fileName, onClose }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const frameRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controls, setControls] = useState({
    rotation: { x: 0, y: 0 },
    zoom: 1,
    autoRotate: false
  });

  // Mouse interaction state
  const mouseState = useRef({
    isDown: false,
    lastX: 0,
    lastY: 0
  });

  const formatLoadError = (err, label = 'model') => {
    const status = err?.target?.status;
    if (status === 0) {
      return `Failed to load ${label}. This is often caused by Storage CORS blocking XHR requests for this origin.`;
    }
    if (typeof status === 'number' && status > 0) {
      return `Failed to load ${label} (HTTP ${status}).`;
    }
    const msg = err?.message || err?.toString?.() || 'Unknown error';
    return `Failed to load ${label}: ${msg}`;
  };

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1A1D21);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, 600 / 400, 0.1, 1000);
    camera.position.z = 5;
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(600, 400);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-10, -10, -5);
    scene.add(directionalLight2);

    mountRef.current.appendChild(renderer.domElement);

    // Load model
    loadModel(modelUrl, scene);

    // Animation loop
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      
      if (modelRef.current) {
        // Auto-rotate if enabled
        if (controls.autoRotate) {
          modelRef.current.rotation.y += 0.005;
        }
        
        // Apply manual rotation
        modelRef.current.rotation.x = controls.rotation.x;
        modelRef.current.rotation.y += controls.rotation.y * 0.01;
        
        // Apply zoom
        camera.position.z = 5 / controls.zoom;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Mouse controls
    const handleMouseDown = (event) => {
      mouseState.current.isDown = true;
      mouseState.current.lastX = event.clientX;
      mouseState.current.lastY = event.clientY;
    };

    const handleMouseMove = (event) => {
      if (!mouseState.current.isDown) return;
      
      const deltaX = event.clientX - mouseState.current.lastX;
      const deltaY = event.clientY - mouseState.current.lastY;
      
      setControls(prev => ({
        ...prev,
        rotation: {
          x: prev.rotation.x + deltaY * 0.01,
          y: prev.rotation.y + deltaX
        }
      }));
      
      mouseState.current.lastX = event.clientX;
      mouseState.current.lastY = event.clientY;
    };

    const handleMouseUp = () => {
      mouseState.current.isDown = false;
    };

    const handleWheel = (event) => {
      event.preventDefault();
      const zoomSpeed = 0.1;
      const newZoom = controls.zoom + (event.deltaY > 0 ? -zoomSpeed : zoomSpeed);
      setControls(prev => ({
        ...prev,
        zoom: Math.max(0.5, Math.min(3, newZoom))
      }));
    };

    const canvas = renderer.domElement;
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);
    canvas.style.cursor = 'grab';

    // Cleanup
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      // Dispose of Three.js objects
      if (modelRef.current) {
        scene.remove(modelRef.current);
      }
      renderer.dispose();
    };
  }, [modelUrl]);

  const loadModel = async (url, scene) => {
    try {
      setLoading(true);
      setError(null);

      // Determine loader based on file extension
      // Handle Firebase Storage URLs with query parameters
      const urlWithoutParams = url.split('?')[0]; // Remove query parameters
      const extension = urlWithoutParams.split('.').pop().toLowerCase();
      console.log('Loading 3D model:', extension, url);

      if (extension === 'gltf' || extension === 'glb') {
        // Dynamically import GLTFLoader
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        const loader = new GLTFLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (gltf) => {
            console.log('GLTF loaded successfully:', gltf);
            const model = gltf.scene || gltf;
            setupModel(model, scene);
          },
          (progress) => {
            if (progress.total > 0) {
              const percentage = (progress.loaded / progress.total * 100).toFixed(1);
              console.log('Loading progress:', percentage + '%');
            }
          },
          (error) => {
            console.error('Error loading GLTF:', error);
            setError(formatLoadError(error, 'GLTF/GLB model'));
            setLoading(false);
          }
        );
      } else if (extension === 'obj') {
        // Dynamically import OBJLoader
        const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
        const loader = new OBJLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (object) => {
            console.log('OBJ loaded successfully:', object);
            setupModel(object, scene);
          },
          (progress) => {
            if (progress.total > 0) {
              const percentage = (progress.loaded / progress.total * 100).toFixed(1);
              console.log('Loading progress:', percentage + '%');
            }
          },
          (error) => {
            console.error('Error loading OBJ:', error);
            setError(formatLoadError(error, 'OBJ model'));
            setLoading(false);
          }
        );
      } else if (extension === 'stl') {
        // Dynamically import STLLoader
        const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
        const loader = new STLLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (geometry) => {
            console.log('STL loaded successfully:', geometry);
            // STL files only contain geometry, so we need to create a mesh
            const material = new THREE.MeshLambertMaterial({ 
              color: 0x3BA6FF,
              side: THREE.DoubleSide // Show both sides for better visibility
            });
            const mesh = new THREE.Mesh(geometry, material);
            setupModel(mesh, scene);
          },
          (progress) => {
            if (progress.total > 0) {
              const percentage = (progress.loaded / progress.total * 100).toFixed(1);
              console.log('Loading progress:', percentage + '%');
            }
          },
          (error) => {
            console.error('Error loading STL:', error);
            setError(formatLoadError(error, 'STL model'));
            setLoading(false);
          }
        );
      } else if (extension === 'fbx') {
        // Dynamically import FBXLoader
        const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js');
        const loader = new FBXLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (object) => {
            console.log('FBX loaded successfully:', object);
            setupModel(object, scene);
          },
          (progress) => {
            if (progress.total > 0) {
              const percentage = (progress.loaded / progress.total * 100).toFixed(1);
              console.log('Loading progress:', percentage + '%');
            }
          },
          (error) => {
            console.error('Error loading FBX:', error);
            setError(formatLoadError(error, 'FBX model'));
            setLoading(false);
          }
        );
      } else if (extension === 'ply') {
        // Dynamically import PLYLoader
        const { PLYLoader } = await import('three/examples/jsm/loaders/PLYLoader.js');
        const loader = new PLYLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (geometry) => {
            console.log('PLY loaded successfully:', geometry);
            const material = new THREE.MeshLambertMaterial({ 
              color: 0x3BA6FF,
              side: THREE.DoubleSide
            });
            const mesh = new THREE.Mesh(geometry, material);
            setupModel(mesh, scene);
          },
          (progress) => {
            if (progress.total > 0) {
              const percentage = (progress.loaded / progress.total * 100).toFixed(1);
              console.log('Loading progress:', percentage + '%');
            }
          },
          (error) => {
            console.error('Error loading PLY:', error);
            setError(formatLoadError(error, 'PLY model'));
            setLoading(false);
          }
        );
      } else {
        // For unsupported formats, show a placeholder cube with more info
        console.log('Unsupported format:', extension);
        const geometry = new THREE.BoxGeometry(2, 2, 2);
        const material = new THREE.MeshLambertMaterial({ 
          color: 0x3BA6FF,
          transparent: true,
          opacity: 0.8,
          wireframe: true
        });
        const cube = new THREE.Mesh(geometry, material);
        setupModel(cube, scene);
        setError(`Format "${extension.toUpperCase()}" preview not supported. Supported formats: GLTF, GLB, OBJ, STL, FBX, PLY`);
      }
    } catch (err) {
      console.error('Error setting up model loader:', err);
      setError(formatLoadError(err, '3D model viewer'));
      setLoading(false);
    }
  };

  const setupModel = (model, scene) => {
    // Center and scale the model
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Scale model to fit in viewport
    const maxSize = Math.max(size.x, size.y, size.z);
    const scale = 3 / maxSize;
    model.scale.multiplyScalar(scale);
    
    // Center the model
    model.position.sub(center.multiplyScalar(scale));
    
    // Add to scene
    scene.add(model);
    modelRef.current = model;
    
    setLoading(false);
  };

  const toggleAutoRotate = () => {
    setControls(prev => ({
      ...prev,
      autoRotate: !prev.autoRotate
    }));
  };

  const resetView = () => {
    setControls({
      rotation: { x: 0, y: 0 },
      zoom: 1,
      autoRotate: false
    });
  };

  return (
    <div 
      className="fullscreen-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#2C3036',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          width: '100%', 
          marginBottom: '16px' 
        }}>
          <h3 style={{ color: '#EAEAEA', margin: 0 }}>{fileName}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#EAEAEA',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            ✕
          </button>
        </div>
        
        {/* 3D Viewer */}
        <div style={{
          position: 'relative',
          backgroundColor: '#1A1D21',
          borderRadius: '8px',
          border: '1px solid #3C4147',
          overflow: 'hidden'
        }}>
          <div ref={mountRef} />
          
          {/* Loading overlay */}
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(26, 29, 33, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#EAEAEA'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid #3BA6FF', 
                  borderTop: '3px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }}></div>
                <p>Loading 3D model...</p>
              </div>
            </div>
          )}
          
          {/* Error overlay */}
          {error && !loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(26, 29, 33, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#EAEAEA',
              padding: '20px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <p>{error}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginTop: '16px',
          alignItems: 'center'
        }}>
          <button
            onClick={toggleAutoRotate}
            style={{
              padding: '8px 16px',
              backgroundColor: controls.autoRotate ? '#3BA6FF' : 'transparent',
              color: controls.autoRotate ? 'white' : '#EAEAEA',
              border: '1px solid #3BA6FF',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {controls.autoRotate ? 'Stop' : 'Auto-rotate'}
          </button>
          
          <button
            onClick={resetView}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: '#EAEAEA',
              border: '1px solid #666',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Reset View
          </button>
          
          <a
            href={modelUrl}
            download={fileName}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px'
            }}
          >
            Download
          </a>
        </div>
        
        {/* Instructions */}
        <div style={{ 
          marginTop: '12px', 
          fontSize: '12px', 
          color: '#9A9FA5', 
          textAlign: 'center' 
        }}>
          <p>Drag to rotate • Scroll to zoom • Supports GLTF, GLB, OBJ, STL, FBX, PLY formats</p>
        </div>
      </div>
    </div>
  );
};

export default ThreeDViewer;
